export class MusicSystem {
  private currentTrack: HTMLAudioElement | null = null;
  private mainTracks: HTMLAudioElement[] = [];
  private lagosTrack: HTMLAudioElement | null = null;
  private currentMainTrackIndex: number = 0;
  private isLagosMode: boolean = false;
  private musicStarted: boolean = false; // Track if music has successfully started

  constructor() {
    // Create audio elements for main tracks
    this.mainTracks = [
      this.createAudioElement('/tunes/goldfinger.mp3'),
      this.createAudioElement('/tunes/ace_of_spades.mp3')
    ];

    // Create audio element for Lagos
    this.lagosTrack = this.createAudioElement('/tunes/lagos.mp3');

    // Set up event listeners for track ending
    this.mainTracks.forEach((track) => {
      track.addEventListener('ended', () => {
        if (!this.isLagosMode) {
          this.playNextMainTrack();
        }
      });
    });

    // Lagos track should not loop automatically - we handle it in the scene
    this.lagosTrack.loop = false;
  }

  private createAudioElement(src: string): HTMLAudioElement {
    const audio = new Audio(src);
    audio.volume = 1.0; // Max volume
    return audio;
  }

  // Start playing the main game music (alternating between goldfinger and ace_of_spades)
  startMainMusic(): void {
    // Don't restart if already playing main music
    if (!this.isLagosMode && this.currentTrack && !this.currentTrack.paused) {
      return;
    }

    this.isLagosMode = false;
    // Randomize the initial track selection
    this.currentMainTrackIndex = Math.floor(Math.random() * this.mainTracks.length);
    this.playMainTrack(this.currentMainTrackIndex);
  }

  private playMainTrack(index: number): void {
    // Stop all tracks first
    this.stopAll();

    // Play the selected main track
    this.currentTrack = this.mainTracks[index];
    console.log('Starting track:', this.currentTrack.src);

    // Ensure the track is loaded and ready
    const playPromise = this.currentTrack.play();

    if (playPromise !== undefined) {
      playPromise.then(() => {
        console.log('Music playing successfully');
        this.musicStarted = true;
      }).catch(error => {
        console.warn('Failed to play music:', error);
        console.warn('This is usually due to browser autoplay policy. User interaction required.');
        this.musicStarted = false;
      });
    }
  }

  private playNextMainTrack(): void {
    // Alternate between the two main tracks
    this.currentMainTrackIndex = (this.currentMainTrackIndex + 1) % this.mainTracks.length;
    this.playMainTrack(this.currentMainTrackIndex);
  }

  // Switch to Lagos music
  playLagosMusic(): void {
    this.isLagosMode = true;
    this.stopAll();

    if (this.lagosTrack) {
      this.currentTrack = this.lagosTrack;
      this.lagosTrack.currentTime = 0; // Reset to start
      console.log('Switching to Lagos music');

      const playPromise = this.lagosTrack.play();

      if (playPromise !== undefined) {
        playPromise.then(() => {
          console.log('Lagos music playing successfully');
          this.musicStarted = true;
        }).catch(error => {
          console.warn('Failed to play Lagos music:', error);
          console.warn('This is usually due to browser autoplay policy. User interaction required.');
          this.musicStarted = false;
        });
      }
    }
  }

  // Return to main music after Lagos
  returnToMainMusic(): void {
    this.isLagosMode = false;
    // Continue from where we left off in the main track rotation
    this.playMainTrack(this.currentMainTrackIndex);
  }

  private stopAll(): void {
    [...this.mainTracks, this.lagosTrack].forEach(track => {
      if (track) {
        track.pause();
        track.currentTime = 0;
      }
    });
  }

  // Stop all music (useful for game over/menu)
  stopMusic(): void {
    this.stopAll();
    this.currentTrack = null;
  }

  // Set volume for all tracks
  setVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    [...this.mainTracks, this.lagosTrack].forEach(track => {
      if (track) {
        track.volume = clampedVolume;
      }
    });
  }

  // Attempt to start music on user interaction (for autoplay policy compliance)
  ensureMusicPlaying(): void {
    // If music hasn't started or isn't playing, try to start it
    if (!this.musicStarted || !this.currentTrack || this.currentTrack.paused) {
      if (!this.isLagosMode) {
        this.startMainMusic();
      }
    }
  }

  // Check if music is currently playing
  isPlaying(): boolean {
    return this.musicStarted && this.currentTrack !== null && !this.currentTrack.paused;
  }
}
